import {
  ALL_CONFIGS,
  AXIOS_TIMEOUT,
  COEFF_PRICE_PUBLIC_IDO,
  DEFAULT_TOKEN_IDO_CURVE,
  DEFAULT_TOKEN_INIT_IDO_CURVE,
  HELIUS_RPC_DEFAULT,
  SOL_DECIMAL,
  TOTAL_SUPPLY_TOKEN,
} from '@/config';
import { fetchJSONDataFromUrl } from '@/program/logListeners/utils';
import { BN } from '@coral-xyz/anchor';
import { PublicKey, PublicKeyInitData } from '@solana/web3.js';
import axios, { AxiosRequestConfig } from 'axios';
import BigNumber from 'bignumber.js';
import {
  CoinInfo,
  HolderInfo,
  MsgInfo,
  RecordInfo,
  ReplyInfo,
  ReportInfo,
  TradeInfo,
  UserInfo,
} from './types';

export const BACKEND_URL_MEME = import.meta.env.VITE_BACKEND_MEME_URL;
export const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
export const BACKEND_MEME_URL =
  import.meta.env.VITE_BACKEND_MEME_URL || 'https://backend-meme.agents.land';
export const BACKEND_AIRDROP_URL =
  import.meta.env.VITE_BACKEND_AIRDROP_URL ||
  'https://airdrop-be-stag.agents.land';
export const DISTILL_BE_URL =
  import.meta.env.VITE_DISTILL_BACKEND_URL ||
  'https://api-dev.distilled.ai/distill';

const headers: Record<string, string> = {
  'ngrok-skip-browser-warning': 'true',
};

export const config: AxiosRequestConfig = {
  timeout: AXIOS_TIMEOUT,
  headers,
};

export const test = async () => {
  const res = await fetch(`${BACKEND_URL}`);
  const data = await res.json();
  console.log(data);
};

export const getUserByWalletAddress = async ({
  baseUrl,
  wallet,
}: {
  baseUrl: string;
  wallet: string;
}): Promise<UserInfo> => {
  try {
    const response = await axios.get(
      `${baseUrl}/user/wallet/${wallet}`,
      config,
    );
    // console.log("response:", response.data);
    return response.data;
  } catch (err) {
    console.log('err getting user: ', err);
    throw new Error(err);
  }
};

export const updateStrategyConfigByCoin = async (
  id: string,
  data: any,
  baseUrl = BACKEND_URL,
): Promise<any> => {
  try {
    const response = await axios.post(
      `${baseUrl}/coin/token/${id}`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    console.log('err updateStrategyConfigByCoin', err);
    return;
  }
};

export const updateUser = async (id: string, data: UserInfo): Promise<any> => {
  try {
    console.log(`${BACKEND_URL}/user/update/${id}`);
    const response = await axios.post(
      `${BACKEND_URL}/user/update/${id}`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const walletConnect = async ({
  data,
}: {
  data: UserInfo;
}): Promise<any> => {
  try {
    const response = await axios.post(`${BACKEND_URL}/user`, data, config);
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const confirmWallet = async ({
  data,
}: {
  data: UserInfo;
}): Promise<any> => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}/user/confirm`,
      data,
      config,
    );
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const getCoinsInfo = async (
  baseUrl,
  params,
): Promise<{ coins: CoinInfo[]; isError?: boolean }> => {
  return baseUrl === BACKEND_MEME_URL
    ? getMemeCoinsInfo(params)
    : getAgentCoinsInfo(params);
};

export const getUpcoming = async (
  params,
): Promise<{ result: any; isError?: boolean }> => {
  return getUpcomingCoins(params);
};

export const getUpcomingCoins = async (
  params,
): Promise<{ result: any; isError?: boolean }> => {
  try {
    const res = await axios.get(`${BACKEND_URL}/upcoming`, {
      ...config,
      params,
    });
    return res.data;
  } catch (error) {
    console.log('getUpcomingCoins from BE failed', error);
    return {
      result: [],
      isError: true,
    };
  }
};

export const getAgentCoinsInfo = async (
  params,
): Promise<{ coins: CoinInfo[]; isError?: boolean }> => {
  try {
    const res = await axios.get(`${BACKEND_URL}/coin`, {
      ...config,
      params,
    });
    return res.data;
  } catch (error) {
    console.log('Get List agent token from BE failed', error);
    return {
      coins: [],
      isError: true,
    };
  }
};

export const getMemeCoinsInfo = async (
  params,
): Promise<{ coins: CoinInfo[]; isError?: boolean }> => {
  try {
    const res = await axios.get(`${BACKEND_MEME_URL}/coin`, {
      ...config,
      params,
    });
    return res.data;
  } catch (error) {
    console.log('Get List meme token from BE failed', error);
    return {
      coins: [],
      isError: true,
    };
  }
};

export const getMemeCoinsDetailByAddress = async (
  tokenAddress,
): Promise<{ tokenAddress: string }> => {
  try {
    const res = await axios.get(`${BACKEND_MEME_URL}/coin/${tokenAddress}`, {
      ...config,
    });
    return res.data;
  } catch (error) {
    console.log('Get List meme token from BE failed', error);
    return;
  }
};

export const getAgentsData = async (params): Promise<CoinInfo[]> => {
  const res = await axios.get(`${DISTILL_BE_URL}/user/search`, {
    ...config,
    params: {
      filter: JSON.stringify({
        username: '',
        status: 1,
        role: 4,
        publish: 1,
        ...(params.filter || {}),
      }),
      limit: 50,
      offset: 0,
    },
  });
  return res.data;
};

export const getAgentsDataByUser = async (params): Promise<any[]> => {
  const res = await axios.get(`${DISTILL_BE_URL}/bot/public/list`, {
    ...config,
    params: {
      filter: JSON.stringify({
        ownerAddress: params.user,
      }),
      limit: 100,
      offset: 0,
    },
  });
  return res.data;
};

export const getCoinsInfoBy = async (
  baseUrl: string,
  id: string,
): Promise<CoinInfo[]> => {
  const res = await axios.get<CoinInfo[]>(`${baseUrl}/coin/user/${id}`, config);
  return res.data.map((info) => ({
    ...info,
    tokenReserves: new BN(info.tokenReserves),
    lamportReserves: new BN(info.lamportReserves),
  }));
};

export const genTokenKeypair = async (baseUrl: string): Promise<string> => {
  const res = await axios.get<{
    privateKey: string;
  }>(`${baseUrl}/keypair`, config);
  return res.data.privateKey;
};

export const getCoinInfo = async (
  baseUrl: string,
  data: string,
): Promise<CoinInfo> => {
  try {
    const response = await axios.get(`${baseUrl}/coin/${data}`, config);

    let metadata = response.data?.metadata;
    if (!metadata && response.data.url) {
      metadata = await fetchJSONDataFromUrl(response.data.url);
    }

    return {
      ...response.data,
      tokenReserves: new BN(response.data.tokenReserves),
      lamportReserves: new BN(response.data.lamportReserves),
      metadata,
      description: metadata?.description,
    };
  } catch (err) {
    console.log('err get coin info: ', err);
    // throw new Error(err);
  }
};

export type RetryOptions = {
  retry?: number;
  timeout?: number;
  callback?: (retry: number) => void;
};

export const fetchRetry = async (
  url: RequestInfo | URL,
  options: RequestInit & RetryOptions = {},
) => {
  let retry = options.retry ?? 3;
  const { callback, timeout = 30000, ...init } = options;
  init.signal = AbortSignal.timeout(timeout);
  while (retry > 0) {
    try {
      return await fetch(url, init);
    } catch (e) {
      callback?.(retry);
      retry--;
      if (retry === 0) {
        throw e;
      }
    }
  }
};

export const getMessageByCoin = async (
  baseUrl: string,
  data: string,
): Promise<MsgInfo[]> => {
  try {
    const response = await axios.get(
      `${baseUrl}/feedback/coin/${data}`,
      config,
    );
    return response.data;
  } catch (err) {
    console.log('err get message by coin: ', err);
  }
};

export const getCoinTrade = async (
  baseUrl: string,
  data: string,
): Promise<TradeInfo> => {
  try {
    const response = await axios.get(`${baseUrl}/cointrade/${data}`, config);
    console.log('trade response::', response);
    return {
      ...response.data,
      record: response.data.record.map((r: RecordInfo) => ({
        ...r,
        lamportAmount: new BN(r.lamportAmount),
        tokenAmount: new BN(r.tokenAmount),
      })),
    };
  } catch (err) {
    console.log('err get coin trade: ', err);
    // throw new Error(err);
  }
};

export const getKoth = async (baseUrl: string): Promise<CoinInfo> => {
  try {
    const response = await axios.get(`${baseUrl}/coin/king/koth`, config);
    return {
      ...response.data,
    };
  } catch (err) {
    console.log('err get coin king: ', err);
    // throw new Error(err);
  }
};

export const postReply = async (baseUrl: string, data: ReplyInfo) => {
  try {
    const response = await axios.post(`${baseUrl}/feedback`, data, config);
    return response.data;
  } catch (err) {
    return { error: 'error setting up the request' };
  }
};

export const reportToken = async (data: ReportInfo) => {
  try {
    const response = await axios.post(`${BACKEND_URL}/report`, data, config);
    return response.data;
  } catch (err) {
    console.log('error reportToken', err);
    return;
  }
};

export const getReportedByUserAndCoin = async (
  wallet: string,
  coinId: string,
) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}/report/coin/${coinId}/user/${wallet}`,
      config,
    );
    return response.data;
  } catch (err) {
    console.log('error get report coin by user request', err);
    return;
  }
};

// ================== Get Holders ===========================
export const findHolders = async (
  mint: string,
  rpcUrl: string = HELIUS_RPC_DEFAULT,
) => {
  let allOwners: HolderInfo[] = [];

  const HELIUS_RPC =
    rpcUrl ||
    import.meta.env.VITE_SOLANA_RPC ||
    'https://devnet.helius-rpc.com/?api-key=44b7171f-7de7-4e68-9d08-eff1ef7529bd';

  // query top 20 holders
  try {
    const response = await fetch(HELIUS_RPC, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'getTokenAccounts',
        id: 'helius-test',
        params: {
          page: 1,
          limit: 20,
          displayOptions: {},
          //mint address for the token we are interested in
          mint: mint,
        },
      }),
    });
    const data = await response.json();
    // error when querying -> return empty holder
    if (!data?.result?.token_accounts) return [];

    // Adding unique owners to a list of token owners.
    data.result.token_accounts.forEach((account) => {
      allOwners.push({
        slice: account.owner.slice(0, 3) + `...` + account.owner.slice(-4),
        owner: account.owner,
        amount: account.amount,
      });
    });

    return allOwners;
  } catch (error) {
    console.log('Error finding token holders: ', error);
    return [];
  }
};

export const findHoldersFromBE = async (
  baseUrl: string,
  mint: string,
  limit: number = 20,
) => {
  try {
    const response = await axios.get(
      `${baseUrl}/coin/token/${mint}/holder?limit=${limit}`,
      config,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching findHoldersFromBE price:', error);
  }
};

export const findMarketCapFromBE = async (baseUrl: string, mint: string) => {
  try {
    const response = await axios.get(
      `${baseUrl}/coin/token/${mint}/market`,
      config,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching findMarketCapFromBE price:', error);
    return;
  }
};

export const getSolPriceInUSD = async () => {
  try {
    // Fetch the price data from CoinGecko
    const response = await axios.get(
      // "https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd"
      'https://price.market.orai.io/simple/price?ids=solana%2Cmax-2&vs_currencies=usd',
    );
    const solPriceInUSD = response.data?.solana?.usd;
    const maxPriceUsd = response.data?.['max-2']?.usd;
    return { solPriceInUSD, maxPriceUsd };
  } catch (error) {
    console.error('Error fetching SOL price:', error);
    throw error;
  }
};

export const getTokensPriceByIds = async (
  tokens: string[],
  cachedPrices = {},
): Promise<any> => {
  if (tokens.length === 0) return cachedPrices;

  const BASE_URL =
    'https://api.geckoterminal.com/api/v2/networks/solana/tokens/multi/';
  const TOKEN_LIST = tokens.map(encodeURIComponent).join(',');
  const fullUrl = BASE_URL + TOKEN_LIST;
  const prices = { ...cachedPrices };
  try {
    const resp = await fetchRetry(fullUrl);
    const rawData = await resp.json();
    // update cached
    for (const key of tokens) {
      const tokenInfo = rawData.data.find((item) => item.attributes.address);
      prices[key] = tokenInfo?.attributes?.price_usd;
    }
  } catch {
    // remain old cache
    console.log('error getTokensPriceByIds: ', prices);
  }
  return prices;
};

// ===========================Functions=====================================
const JWT = import.meta.env.VITE_PINATA_PRIVATE_KEY;

export const pinFileToIPFS = async (blob: File) => {
  try {
    const data = new FormData();
    data.append('file', blob);
    const res = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${JWT}`,
      },
      body: data,
    });
    const resData = await res.json();
    return resData;
  } catch (error) {
    console.log(error);
  }
};
export const uploadImagePinata = async (url: string) => {
  const res = await fetch(url);
  console.log(res.blob);
  const blob = await res.blob();

  const imageFile = new File([blob], 'image.png', { type: 'image/png' });
  console.log(imageFile);
  const resData = await pinFileToIPFS(imageFile);
  console.log(resData, 'RESDATA>>>>');
  if (resData) {
    return `https://gateway.pinata.cloud/ipfs/${resData.IpfsHash}`;
  } else {
    return false;
  }
};

/**
 * Returns a shortened string by replacing characters in between with '...'.
 * @param str The input string.
 * @param from The position of the character to be kept as-is in the resulting string.
 * @param end The number of characters to be kept as-is at the end of the resulting string.
 * @returns The shortened string, or '-' if the input string is null or undefined.
 */
export const reduceString = (
  str: string,
  from: number = 4,
  end: number = 4,
) => {
  if (!str) {
    return '-';
  }

  return str && typeof str.substring === 'function'
    ? str.substring(0, from) + '...' + str.substring(str.length - end)
    : '-';
};

export const toBig = (value: number, decimals: number): BN => {
  if (!value) return new BN(0);
  return new BN(value).mul(new BN(10 ** decimals));
};

export const fromBig = (value: BN, decimals: number = 6): number => {
  if (!value) return 0;
  if (!(value instanceof BN)) {
    value = new BN(value);
  }
  const divmodResult = value.divmod(new BN(10 ** decimals));
  const result =
    divmodResult.div.toString() +
    '.' +
    divmodResult.mod.toString().padStart(decimals, '0');
  return parseFloat(result);
};

export const calculateTokenPrice = (
  tokenReserves: BN,
  lamportReserves: BN,
  tokenDecimals: number,
  solPriceinUSD?: number,
): number => {
  if (!(tokenReserves instanceof BN)) {
    tokenReserves = new BN(tokenReserves);
  }
  if (!(lamportReserves instanceof BN)) {
    lamportReserves = new BN(lamportReserves);
  }
  const tokenReservesNum = fromBig(tokenReserves, tokenDecimals);
  const lamportReservesNum = fromBig(lamportReserves, 9);
  return ((solPriceinUSD || 1) * lamportReservesNum) / tokenReservesNum;
};

export function calculateKotHProgress(
  lamportReserves: BN,
  bondingCurveLimit: BN,
  initCurve: number = ALL_CONFIGS.INIT_SOL_BONDING_CURVE,
) {
  if (!(lamportReserves instanceof BN)) {
    lamportReserves = new BN(lamportReserves);
  }
  if (!(bondingCurveLimit instanceof BN)) {
    bondingCurveLimit = new BN(bondingCurveLimit);
  }
  if (bondingCurveLimit.toNumber() === 0) return 0;
  const calcLamportReserves = lamportReserves.sub(new BN(initCurve));
  const calcBondingCurveLimit = bondingCurveLimit.sub(new BN(initCurve));
  let currentKotHProgress =
    (fromBig(calcLamportReserves, 9) /
      (fromBig(calcBondingCurveLimit, 9) / 2)) *
    100;

  if (currentKotHProgress > 100) currentKotHProgress = 100;
  return currentKotHProgress;
}

export const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const retrieveEnvVariable = (variableName: string) => {
  const variable = process.env[variableName] || '';
  if (!variable) {
    console.log(`${variableName} is not set`);
    // process.exit(1);
  }
  return variable;
};

export const toBN = (val: BigNumber.Value) => {
  return new BigNumber(val);
};

export const toPublicKey = (val: PublicKeyInitData) => {
  try {
    return new PublicKey(val);
  } catch (error) {
    console.log('error to PublicKey', error);
    return null;
  }
};

export const calculateMarketCap = (
  tokenReserves: BN,
  decimals: number,
  tokenPrice: number,
): number => {
  return TOTAL_SUPPLY_TOKEN * tokenPrice;
  // if (!(tokenReserves instanceof BN)) {
  //   tokenReserves = new BN(tokenReserves);
  // }
  // const tokenReservesNum = fromBig(tokenReserves, decimals);
  // return tokenReservesNum * tokenPrice;
};

export function formatTimePeriod(seconds: number) {
  const units = [
    { label: 'second', value: 1 },
    { label: 'minute', value: 60 },
    { label: 'hour', value: 3600 },
    { label: 'day', value: 86400 },
    { label: 'week', value: 604800 },
    // { label: 'month', value: 2629800 }, // Approximate (30.44 days)
    { label: 'month', value: 2592000 }, // Approximate (30 days)
    { label: 'year', value: 31557600 }, // Approximate (365.25 days)
  ];

  for (let i = units.length - 1; i >= 0; i--) {
    const unit = units[i];
    if (seconds >= unit.value) {
      const count = Math.floor(seconds / unit.value);
      return `${count} ${unit.label}${count > 1 ? 's' : ''}`;
    }
  }
  return 'just now';
}

export const calcIdoPrice = (curveInfo, onParty, onPublic) => {
  if (!curveInfo.partyAmount) {
    return 0;
  }

  const initPrice = curveInfo.curveLimit / curveInfo.partyAmount / 10 ** 3;
  // div for delta decimal

  if (onParty || !onPublic) {
    return initPrice;
  }
  return initPrice * COEFF_PRICE_PUBLIC_IDO; // on public round must to mul 1.2, this coeff defined on contract
};

export const simulateBuyIdo = (price, solAmt) => {
  if (!price) {
    return '0';
  }

  return toBN(solAmt).multipliedBy(0.99).div(price).toString();
};

export const simulateSellIdo = (price, tokenAmt) => {
  if (!price) {
    return '0';
  }

  return toBN(tokenAmt).multipliedBy(0.99).multipliedBy(price).toString();
};

export const calcIdoCoinPrice = (coinItem, curveInfo?: any) => {
  const partyStart = Math.ceil(
    new Date(coinItem?.partyTradingTime || Date.now()).getTime() /
      ALL_CONFIGS.TIMER.MILLISECOND,
  );
  const publicStart = Math.floor(
    new Date(coinItem?.publicTradingTime || Date.now()).getTime() /
      ALL_CONFIGS.TIMER.MILLISECOND,
  );

  const isPartyStart =
    partyStart && partyStart * ALL_CONFIGS.TIMER.MILLISECONDS <= Date.now();
  const isPublicStart =
    publicStart && publicStart * ALL_CONFIGS.TIMER.MILLISECONDS <= Date.now();
  const isOnParty = !isPublicStart && isPartyStart;

  const idoPrice = calcIdoPrice(
    {
      partyAmount:
        curveInfo?.partyAmount?.toNumber() ||
        DEFAULT_TOKEN_IDO_CURVE * 10 ** 12,
      curveLimit:
        curveInfo?.['curveLimit']?.toNumber() ||
        coinItem?.bondingCurveLimit ||
        0,
    },
    isOnParty,
    isPublicStart,
  );

  return idoPrice;
};

export const calcBondingCurveOfIDO = (
  tokenReserve,
  tokenInit = DEFAULT_TOKEN_INIT_IDO_CURVE * 10 ** 12,
  tokenOnCurve = DEFAULT_TOKEN_IDO_CURVE * 10 ** 12,
) => {
  return toBN(
    // sold token amount
    toBN(tokenInit).minus(
      tokenReserve?.toString() || DEFAULT_TOKEN_INIT_IDO_CURVE,
    ),
  )
    .multipliedBy(100)
    .div(tokenOnCurve)
    .toNumber();
};

export const simulateOutIdo = (coin, maxAmount, price, fee = 0.01) => {
  if (!coin) return 0;
  return toBN(maxAmount)
    .multipliedBy(price)
    .div(1 - fee)
    .multipliedBy(10 ** SOL_DECIMAL)
    .toNumber();
};
